import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { Phone, PhoneOff, Video } from 'lucide-react';
import { createElement } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useRealtime } from './RealtimeContext';
import { useToast } from './ToastContext';
import IncomingCallModal from '../components/video/IncomingCallModal';
import ActiveCallView from '../components/video/ActiveCallView';
import { createRingback, createRingtone } from '../utils/callAudio';
import { webrtcApi } from '../services/api';
import type { CallSignal, PublicUser, RealtimeEvent } from '../types';

const CALL_TIMEOUT_MS = 40_000;
// How long we tolerate a transient ICE drop before treating the call as failed.
const RECONNECT_GRACE_MS = 15_000;
// Cap upstream video so calls stay usable on slow / metered connections.
const MAX_VIDEO_BITRATE = 1_200_000;
// Zoom levels offered as buttons. 0.5x is the ultra-wide lens (a separate
// camera device); 1x/2x/3x are applied via the track zoom constraint.
const ZOOM_PRESETS = [0.5, 1, 2, 3];

const VIDEO_SIZE: MediaTrackConstraints = {
  width: { ideal: 1280, max: 1920 },
  height: { ideal: 720, max: 1080 },
  frameRate: { ideal: 30, max: 30 },
};

const ICE_SERVERS: RTCConfiguration = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ],
};

export type CallStatus = 'idle' | 'outgoing' | 'incoming' | 'active' | 'ended';
export type ConnectionPhase = 'connecting' | 'connected' | 'reconnecting';
export type FacingMode = 'user' | 'environment';
export type VideoFit = 'cover' | 'contain';

interface VideoCallContextValue {
  status: CallStatus;
  connectionPhase: ConnectionPhase;
  remoteUser: PublicUser | null;
  startCall: (user: PublicUser) => Promise<void>;
  acceptCall: () => Promise<void>;
  rejectCall: () => void;
  endCall: () => void;
  muted: boolean;
  cameraOff: boolean;
  toggleMute: () => void;
  toggleCamera: () => void;
  // Camera selection
  facingMode: FacingMode;
  canSwitchCamera: boolean;
  switchingCamera: boolean;
  switchCamera: () => Promise<void>;
  // Zoom presets (0.5x / 1x / 2x / 3x)
  zoomPresets: number[];
  activeZoom: number;
  applyZoom: (preset: number) => void;
  // Layout
  swapped: boolean;
  toggleSwap: () => void;
  videoFit: VideoFit;
  toggleFit: () => void;
}

interface ZoomCaps {
  min: number;
  max: number;
}

const VideoCallContext = createContext<VideoCallContextValue | null>(null);

export function useVideoCall(): VideoCallContextValue {
  const ctx = useContext(VideoCallContext);
  if (!ctx) throw new Error('useVideoCall must be used within VideoCallProvider');
  return ctx;
}

function newCallId(): string {
  return crypto.randomUUID();
}

function peerFromSignal(
  profile: PublicUser | null | undefined,
  userId: string
): PublicUser {
  if (profile?.id) return profile;
  return { id: userId, name: null, email: 'Friend', avatarUrl: null };
}

// Read the zoom range a track supports, if any (mobile rear cameras).
function readZoomCaps(track: MediaStreamTrack | undefined): ZoomCaps | null {
  const caps = (track?.getCapabilities?.() ?? {}) as MediaTrackCapabilities & {
    zoom?: { min: number; max: number };
  };
  if (caps.zoom && typeof caps.zoom.max === 'number' && caps.zoom.max > (caps.zoom.min ?? 1)) {
    return { min: caps.zoom.min ?? 1, max: caps.zoom.max };
  }
  return null;
}

export function VideoCallProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { subscribe, send } = useRealtime();
  const { showToast } = useToast();

  const [status, setStatus] = useState<CallStatus>('idle');
  const [connectionPhase, setConnectionPhase] = useState<ConnectionPhase>('connecting');
  const [remoteUser, setRemoteUser] = useState<PublicUser | null>(null);
  const [incomingOffer, setIncomingOffer] = useState<RTCSessionDescriptionInit | null>(null);
  const [muted, setMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [facingMode, setFacingMode] = useState<FacingMode>('user');
  const [canSwitchCamera, setCanSwitchCamera] = useState(false);
  const [switchingCamera, setSwitchingCamera] = useState(false);
  const [zoomPresets, setZoomPresets] = useState<number[]>([]);
  const [activeZoom, setActiveZoom] = useState(1);
  const [swapped, setSwapped] = useState(false);
  const [videoFit, setVideoFit] = useState<VideoFit>('cover');

  const callIdRef = useRef<string | null>(null);
  const statusRef = useRef<CallStatus>('idle');
  statusRef.current = status;
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const pendingCandidatesRef = useRef<RTCIceCandidateInit[]>([]);
  const ringtoneRef = useRef<ReturnType<typeof createRingtone> | null>(null);
  const ringbackRef = useRef<ReturnType<typeof createRingback> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isCallerRef = useRef(false);
  // Camera / reliability refs
  const remoteIdRef = useRef<string | null>(null);
  const facingModeRef = useRef<FacingMode>('user');
  const cameraOffRef = useRef(false);
  const videoSenderRef = useRef<RTCRtpSender | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const iceRestartAttemptsRef = useRef(0);
  // Discovered camera devices (populated once permission is granted).
  const frontIdsRef = useRef<string[]>([]);
  const backIdsRef = useRef<string[]>([]);
  const mainBackIdRef = useRef<string | null>(null);
  const wideBackIdRef = useRef<string | null>(null);
  const currentCamIdRef = useRef<string | null>(null);

  const stopSounds = useCallback(() => {
    ringtoneRef.current?.stop();
    ringtoneRef.current = null;
    ringbackRef.current?.stop();
    ringbackRef.current = null;
  }, []);

  const clearCallTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const cleanupMedia = useCallback(() => {
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;
    remoteStreamRef.current = null;
    videoSenderRef.current = null;
    setLocalStream(null);
    setRemoteStream(null);
    pcRef.current?.close();
    pcRef.current = null;
    pendingCandidatesRef.current = [];
    setMuted(false);
    setCameraOff(false);
    cameraOffRef.current = false;
    setFacingMode('user');
    facingModeRef.current = 'user';
    setCanSwitchCamera(false);
    setSwitchingCamera(false);
    setZoomPresets([]);
    setActiveZoom(1);
    setSwapped(false);
    setVideoFit('cover');
    setConnectionPhase('connecting');
    iceRestartAttemptsRef.current = 0;
    frontIdsRef.current = [];
    backIdsRef.current = [];
    mainBackIdRef.current = null;
    wideBackIdRef.current = null;
    currentCamIdRef.current = null;
  }, []);

  const sendSignal = useCallback(
    (signal: Omit<CallSignal, 'from'>) => {
      if (!user?.id) return;
      send({ kind: 'call_signal', signal: { ...signal, from: user.id } });
    },
    [send, user?.id]
  );

  const resetCallState = useCallback(() => {
    stopSounds();
    clearCallTimeout();
    clearReconnectTimer();
    cleanupMedia();
    callIdRef.current = null;
    remoteIdRef.current = null;
    isCallerRef.current = false;
    setIncomingOffer(null);
    setRemoteUser(null);
    setStatus('idle');
  }, [cleanupMedia, clearCallTimeout, clearReconnectTimer, stopSounds]);

  const endCall = useCallback(() => {
    const callId = callIdRef.current;
    const peerId = remoteIdRef.current;
    if (callId && peerId && user?.id) {
      sendSignal({ type: 'call_end', callId, to: peerId });
    }
    resetCallState();
  }, [resetCallState, sendSignal, user?.id]);

  const handleCallTimeout = useCallback(() => {
    const callId = callIdRef.current;
    const peerId = remoteIdRef.current;
    if (!callId || !peerId) {
      resetCallState();
      return;
    }
    if (isCallerRef.current && statusRef.current === 'outgoing') {
      sendSignal({ type: 'call_end', callId, to: peerId });
      showToast({
        title: 'No answer',
        description: 'Your call was not answered.',
        icon: createElement(PhoneOff, { className: 'h-5 w-5 text-gray-500' }),
      });
    } else if (!isCallerRef.current && statusRef.current === 'incoming') {
      sendSignal({ type: 'call_reject', callId, to: peerId });
    }
    resetCallState();
  }, [resetCallState, sendSignal, showToast]);

  const startCallTimeout = useCallback(() => {
    clearCallTimeout();
    timeoutRef.current = setTimeout(handleCallTimeout, CALL_TIMEOUT_MS);
  }, [clearCallTimeout, handleCallTimeout]);

  // Re-negotiate ICE on the caller side after a drop. The callee answers re-offers
  // for the same callId (see the offer handler below), so this re-establishes media.
  const restartIce = useCallback(async () => {
    const pc = pcRef.current;
    const peerId = remoteIdRef.current;
    const callId = callIdRef.current;
    if (!pc || !peerId || !callId || !isCallerRef.current) return;
    if (iceRestartAttemptsRef.current >= 2) {
      endCall();
      return;
    }
    iceRestartAttemptsRef.current += 1;
    try {
      const offer = await pc.createOffer({ iceRestart: true });
      await pc.setLocalDescription(offer);
      sendSignal({ type: 'call_offer', callId, to: peerId, sdp: offer });
    } catch {
      // If we can't even build a restart offer, give the grace timer a chance.
    }
  }, [endCall, sendSignal]);

  // Cap upstream video bitrate and prefer a steady framerate on poor links.
  const configureBandwidth = useCallback(() => {
    const sender = videoSenderRef.current;
    if (!sender) return;
    try {
      const params = sender.getParameters();
      if (!params.encodings || params.encodings.length === 0) {
        params.encodings = [{}];
      }
      params.encodings[0].maxBitrate = MAX_VIDEO_BITRATE;
      params.degradationPreference = 'balanced';
      void sender.setParameters(params).catch(() => {});
    } catch {
      // setParameters not supported on this browser — browser still adapts.
    }
  }, []);

  const setupPeerConnection = useCallback(
    (peerId: string, callId: string, config: RTCConfiguration) => {
      const pc = new RTCPeerConnection(config);
      pcRef.current = pc;

      pc.onicecandidate = (e) => {
        if (e.candidate) {
          sendSignal({
            type: 'ice_candidate',
            callId,
            to: peerId,
            candidate: e.candidate.toJSON(),
          });
        }
      };

      pc.ontrack = (e) => {
        let stream = remoteStreamRef.current;
        if (!stream) {
          stream = new MediaStream();
          remoteStreamRef.current = stream;
        }
        if (!stream.getTracks().some((t) => t.id === e.track.id)) {
          stream.addTrack(e.track);
        }
        setRemoteStream(stream);
        setStatus('active');
        stopSounds();
        clearCallTimeout();
      };

      pc.onconnectionstatechange = () => {
        const state = pc.connectionState;
        if (state === 'connected') {
          iceRestartAttemptsRef.current = 0;
          clearReconnectTimer();
          setConnectionPhase('connected');
        } else if (state === 'disconnected') {
          setConnectionPhase('reconnecting');
          clearReconnectTimer();
          reconnectTimerRef.current = setTimeout(() => {
            if (pcRef.current?.connectionState === 'connected') return;
            void restartIce();
          }, RECONNECT_GRACE_MS);
        } else if (state === 'failed') {
          setConnectionPhase('reconnecting');
          void restartIce();
        }
      };

      pc.oniceconnectionstatechange = () => {
        if (pc.iceConnectionState === 'failed') {
          setConnectionPhase('reconnecting');
          void restartIce();
        }
      };

      return pc;
    },
    [clearCallTimeout, clearReconnectTimer, restartIce, sendSignal, stopSounds]
  );

  // Catalogue the device's cameras. Labels are only populated after the user has
  // granted camera permission, so this must run after the first getUserMedia.
  const enumerateCameras = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const vids = devices.filter((d) => d.kind === 'videoinput');
      const fronts = vids.filter((d) => /front|user|face|self/i.test(d.label));
      const backs = vids.filter((d) => /back|rear|environment|world/i.test(d.label));
      frontIdsRef.current = (fronts.length ? fronts : vids).map((d) => d.deviceId).filter(Boolean);
      backIdsRef.current = backs.map((d) => d.deviceId).filter(Boolean);
      mainBackIdRef.current = backIdsRef.current[0] ?? null;
      // A second rear camera is, on most phones, the ultra-wide (0.5x) lens.
      wideBackIdRef.current = backIdsRef.current[1] ?? null;
      const distinct = new Set(vids.map((d) => d.deviceId).filter(Boolean));
      // We can switch if there's a front+back pair, or simply 2+ cameras.
      setCanSwitchCamera(
        (frontIdsRef.current.length > 0 && backIdsRef.current.length > 0) || distinct.size > 1
      );
    } catch {
      setCanSwitchCamera(false);
    }
  }, []);

  // Recompute which zoom presets are achievable for the current camera, and the
  // current active zoom, based on the live track's capabilities.
  const refreshZoomState = useCallback((stream: MediaStream | null) => {
    const track = stream?.getVideoTracks()[0];
    const caps = readZoomCaps(track);
    const onWide =
      facingModeRef.current === 'environment' &&
      !!wideBackIdRef.current &&
      currentCamIdRef.current === wideBackIdRef.current;

    const hasWideLens =
      (caps && caps.min <= 0.5 + 1e-6) ||
      (facingModeRef.current === 'environment' && !!wideBackIdRef.current);
    const presets = ZOOM_PRESETS.filter((p) => {
      if (p < 1) return hasWideLens; // 0.5x needs an ultra-wide lens
      if (p === 1) return true; // always offer 1x
      return !!caps && caps.max >= p - 1e-6; // 2x/3x within zoom range
    });
    setZoomPresets(presets);

    if (onWide) {
      setActiveZoom(0.5);
    } else {
      const settings = (track?.getSettings?.() ?? {}) as MediaTrackSettings & { zoom?: number };
      setActiveZoom(settings.zoom && settings.zoom > 0 ? settings.zoom : 1);
    }
  }, []);

  // Acquire a new video track for the given constraints and swap it into both the
  // local preview and the peer connection (no renegotiation needed).
  const swapVideoTrack = useCallback(async (videoConstraints: MediaTrackConstraints) => {
    // Release the current camera first — most phones allow only one open at a time.
    const old = localStreamRef.current?.getVideoTracks()[0];
    old?.stop();

    const fresh = await navigator.mediaDevices.getUserMedia({
      video: { ...VIDEO_SIZE, ...videoConstraints },
      audio: false,
    });
    const newVideo = fresh.getVideoTracks()[0];
    newVideo.enabled = !cameraOffRef.current;

    if (videoSenderRef.current) {
      await videoSenderRef.current.replaceTrack(newVideo);
    }

    const audio = localStreamRef.current?.getAudioTracks()[0];
    const combined = new MediaStream();
    if (audio) combined.addTrack(audio);
    combined.addTrack(newVideo);
    localStreamRef.current = combined;
    setLocalStream(combined);
    currentCamIdRef.current = (newVideo.getSettings?.().deviceId as string) ?? null;
    return newVideo;
  }, []);

  const getLocalMedia = useCallback(
    async (facing: FacingMode = 'user') => {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: { facingMode: facing, ...VIDEO_SIZE },
      });
      localStreamRef.current = stream;
      facingModeRef.current = facing;
      setFacingMode(facing);
      setLocalStream(stream);
      currentCamIdRef.current = (stream.getVideoTracks()[0]?.getSettings?.().deviceId as string) ?? null;
      // Now that permission is granted, device labels are readable.
      await enumerateCameras();
      refreshZoomState(stream);
      return stream;
    },
    [enumerateCameras, refreshZoomState]
  );

  const addLocalTracks = useCallback(
    (pc: RTCPeerConnection, stream: MediaStream) => {
      stream.getTracks().forEach((track) => {
        const sender = pc.addTrack(track, stream);
        if (track.kind === 'video') videoSenderRef.current = sender;
      });
      configureBandwidth();
    },
    [configureBandwidth]
  );

  const flushPendingCandidates = useCallback(async (pc: RTCPeerConnection) => {
    const pending = pendingCandidatesRef.current;
    pendingCandidatesRef.current = [];
    for (const c of pending) {
      try {
        await pc.addIceCandidate(c);
      } catch {
        // ignore stale candidates
      }
    }
  }, []);

  // Fetch ICE servers (STUN + TURN if the backend has it) with a STUN fallback.
  const resolveIceConfig = useCallback(async (): Promise<RTCConfiguration> => {
    try {
      const { iceServers } = await webrtcApi.getIceServers();
      if (iceServers && iceServers.length > 0) return { iceServers };
    } catch {
      // Backend unreachable or TURN not configured — fall back to public STUN.
    }
    return ICE_SERVERS;
  }, []);

  const startCall = useCallback(
    async (peer: PublicUser) => {
      if (statusRef.current !== 'idle' || !user?.id) return;

      const callId = newCallId();
      callIdRef.current = callId;
      remoteIdRef.current = peer.id;
      isCallerRef.current = true;
      setRemoteUser(peer);
      setStatus('outgoing');
      setConnectionPhase('connecting');

      ringbackRef.current = createRingback();
      ringbackRef.current.start();
      startCallTimeout();

      try {
        const [stream, iceConfig] = await Promise.all([
          getLocalMedia('user'),
          resolveIceConfig(),
        ]);
        const pc = setupPeerConnection(peer.id, callId, iceConfig);
        addLocalTracks(pc, stream);

        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        sendSignal({
          type: 'call_offer',
          callId,
          to: peer.id,
          sdp: offer,
        });
      } catch {
        resetCallState();
        showToast({
          title: 'Could not start call',
          description: 'Camera/microphone permission may be denied.',
          icon: createElement(Video, { className: 'h-5 w-5 text-red-500' }),
        });
      }
    },
    [
      addLocalTracks,
      getLocalMedia,
      resetCallState,
      resolveIceConfig,
      sendSignal,
      setupPeerConnection,
      showToast,
      startCallTimeout,
      user?.id,
    ]
  );

  const acceptCall = useCallback(async () => {
    if (statusRef.current !== 'incoming' || !incomingOffer || !remoteUser?.id || !callIdRef.current) {
      return;
    }

    const callId = callIdRef.current;
    const peerId = remoteUser.id;
    stopSounds();
    clearCallTimeout();

    try {
      const [stream, iceConfig] = await Promise.all([
        getLocalMedia('user'),
        resolveIceConfig(),
      ]);
      const pc = setupPeerConnection(peerId, callId, iceConfig);
      addLocalTracks(pc, stream);

      await pc.setRemoteDescription(incomingOffer);
      await flushPendingCandidates(pc);

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      sendSignal({
        type: 'call_answer',
        callId,
        to: peerId,
        sdp: answer,
      });
      setIncomingOffer(null);
      setStatus('active');
    } catch {
      resetCallState();
      showToast({
        title: 'Could not join call',
        description: 'Camera/microphone permission may be denied.',
        icon: createElement(PhoneOff, { className: 'h-5 w-5 text-red-500' }),
      });
    }
  }, [
    addLocalTracks,
    clearCallTimeout,
    flushPendingCandidates,
    getLocalMedia,
    incomingOffer,
    remoteUser,
    resetCallState,
    resolveIceConfig,
    sendSignal,
    setupPeerConnection,
    showToast,
    stopSounds,
  ]);

  const rejectCall = useCallback(() => {
    const callId = callIdRef.current;
    const peerId = remoteIdRef.current;
    if (callId && peerId) {
      sendSignal({ type: 'call_reject', callId, to: peerId });
    }
    resetCallState();
  }, [resetCallState, sendSignal]);

  const toggleMute = useCallback(() => {
    const audio = localStreamRef.current?.getAudioTracks()[0];
    if (audio) {
      audio.enabled = !audio.enabled;
      setMuted(!audio.enabled);
    }
  }, []);

  const toggleCamera = useCallback(() => {
    const video = localStreamRef.current?.getVideoTracks()[0];
    if (video) {
      video.enabled = !video.enabled;
      cameraOffRef.current = !video.enabled;
      setCameraOff(!video.enabled);
    }
  }, []);

  // Switch between front and rear cameras. Uses explicit deviceIds (reliable on
  // Chrome/Android, which often ignores a plain facingMode hint), with a
  // facingMode fallback when device labels weren't available.
  const switchCamera = useCallback(async () => {
    if (switchingCamera || !localStreamRef.current) return;
    const next: FacingMode = facingModeRef.current === 'user' ? 'environment' : 'user';
    setSwitchingCamera(true);

    const targetId =
      next === 'environment' ? mainBackIdRef.current ?? backIdsRef.current[0] : frontIdsRef.current[0];

    try {
      await swapVideoTrack(
        targetId ? { deviceId: { exact: targetId } } : { facingMode: { ideal: next } }
      );
      facingModeRef.current = next;
      setFacingMode(next);
      refreshZoomState(localStreamRef.current);
    } catch {
      // deviceId path failed — retry once with a plain facingMode hint.
      try {
        await swapVideoTrack({ facingMode: { ideal: next } });
        facingModeRef.current = next;
        setFacingMode(next);
        refreshZoomState(localStreamRef.current);
      } catch {
        // Restore whatever we can on the original facing.
        try {
          await swapVideoTrack({ facingMode: { ideal: facingModeRef.current } });
          refreshZoomState(localStreamRef.current);
        } catch {
          // leave call running with audio only
        }
        showToast({
          title: 'Camera switch failed',
          description: 'Could not open the other camera on this device.',
          icon: createElement(Video, { className: 'h-5 w-5 text-red-500' }),
        });
      }
    } finally {
      setSwitchingCamera(false);
    }
  }, [refreshZoomState, showToast, swapVideoTrack, switchingCamera]);

  // Apply a zoom preset. 0.5x switches to the ultra-wide rear lens (when present);
  // 1x/2x/3x apply the zoom constraint on the active lens.
  const applyZoom = useCallback(
    async (preset: number) => {
      if (switchingCamera) return;
      const track = localStreamRef.current?.getVideoTracks()[0];
      if (!track) return;

      // --- 0.5x (ultra-wide) ---
      if (preset < 1) {
        const caps = readZoomCaps(track);
        if (caps && caps.min <= 0.5 + 1e-6) {
          await track
            .applyConstraints({ advanced: [{ zoom: Math.max(caps.min, 0.5) } as MediaTrackConstraintSet] })
            .catch(() => {});
          setActiveZoom(0.5);
          return;
        }
        if (facingModeRef.current === 'environment' && wideBackIdRef.current) {
          setSwitchingCamera(true);
          try {
            const newTrack = await swapVideoTrack({ deviceId: { exact: wideBackIdRef.current } });
            const wideCaps = readZoomCaps(newTrack);
            if (wideCaps) {
              await newTrack
                .applyConstraints({ advanced: [{ zoom: wideCaps.min } as MediaTrackConstraintSet] })
                .catch(() => {});
            }
            refreshZoomState(localStreamRef.current);
            setActiveZoom(0.5);
          } catch {
            showToast({
              title: 'Ultra-wide unavailable',
              description: 'This device did not expose a 0.5x lens.',
              icon: createElement(Video, { className: 'h-5 w-5 text-red-500' }),
            });
          } finally {
            setSwitchingCamera(false);
          }
        }
        return;
      }

      // --- 1x / 2x / 3x ---
      // If we're on the ultra-wide lens, return to the main rear lens first.
      if (
        facingModeRef.current === 'environment' &&
        wideBackIdRef.current &&
        currentCamIdRef.current === wideBackIdRef.current &&
        mainBackIdRef.current
      ) {
        setSwitchingCamera(true);
        try {
          await swapVideoTrack({ deviceId: { exact: mainBackIdRef.current } });
          refreshZoomState(localStreamRef.current);
        } catch {
          // stay on current lens
        } finally {
          setSwitchingCamera(false);
        }
      }

      const active = localStreamRef.current?.getVideoTracks()[0];
      const caps = readZoomCaps(active);
      if (active && caps) {
        const z = Math.min(Math.max(preset, caps.min), caps.max);
        await active
          .applyConstraints({ advanced: [{ zoom: z } as MediaTrackConstraintSet] })
          .catch(() => {});
      }
      setActiveZoom(preset);
    },
    [refreshZoomState, showToast, swapVideoTrack, switchingCamera]
  );

  const toggleSwap = useCallback(() => setSwapped((s) => !s), []);
  const toggleFit = useCallback(
    () => setVideoFit((f) => (f === 'cover' ? 'contain' : 'cover')),
    []
  );

  // Handle incoming signaling
  useEffect(() => {
    const unsub = subscribe(async (event: RealtimeEvent) => {
      if (event.kind === 'call_error') {
        if (statusRef.current !== 'idle') {
          resetCallState();
          showToast({
            title: 'Call failed',
            description: event.error || 'Could not connect the call.',
            icon: createElement(PhoneOff, { className: 'h-5 w-5 text-red-500' }),
          });
        }
        return;
      }

      if (event.kind !== 'call_signal') return;
      const { signal } = event;

      if (signal.type === 'call_offer') {
        // A re-offer for the call we're already on = ICE restart / renegotiation.
        if (
          statusRef.current === 'active' &&
          signal.callId === callIdRef.current &&
          pcRef.current &&
          signal.sdp
        ) {
          const pc = pcRef.current;
          try {
            await pc.setRemoteDescription(signal.sdp);
            await flushPendingCandidates(pc);
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);
            sendSignal({ type: 'call_answer', callId: signal.callId, to: signal.from, sdp: answer });
          } catch {
            // ignore — the caller will retry or time out
          }
          return;
        }

        if (statusRef.current !== 'idle') {
          sendSignal({ type: 'call_busy', callId: signal.callId, to: signal.from });
          return;
        }
        callIdRef.current = signal.callId;
        remoteIdRef.current = signal.from;
        isCallerRef.current = false;
        const peer = peerFromSignal(event.from, signal.from);
        setRemoteUser(peer);
        setIncomingOffer(signal.sdp ?? null);
        setStatus('incoming');
        setConnectionPhase('connecting');
        pendingCandidatesRef.current = [];

        ringtoneRef.current = createRingtone();
        ringtoneRef.current.start();
        startCallTimeout();

        showToast({
          title: 'Incoming video call',
          description: peer.name || peer.email || 'A friend is calling',
          icon: createElement(Phone, { className: 'h-5 w-5 text-green-500 animate-pulse' }),
        });
        return;
      }

      if (signal.callId !== callIdRef.current) return;

      if (signal.type === 'call_answer' && signal.sdp) {
        const pc = pcRef.current;
        if (!pc) return;
        try {
          await pc.setRemoteDescription(signal.sdp);
          await flushPendingCandidates(pc);
          setStatus('active');
          stopSounds();
          clearCallTimeout();
        } catch {
          resetCallState();
        }
        return;
      }

      if (signal.type === 'ice_candidate' && signal.candidate) {
        const pc = pcRef.current;
        if (!pc || !pc.remoteDescription) {
          pendingCandidatesRef.current.push(signal.candidate);
          return;
        }
        try {
          await pc.addIceCandidate(signal.candidate);
        } catch {
          // ignore
        }
        return;
      }

      if (signal.type === 'call_end' || signal.type === 'call_reject') {
        if (signal.type === 'call_reject' && isCallerRef.current) {
          showToast({
            title: 'Call declined',
            description: 'They declined your video call.',
            icon: createElement(PhoneOff, { className: 'h-5 w-5 text-gray-500' }),
          });
        }
        resetCallState();
        return;
      }

      if (signal.type === 'call_busy') {
        resetCallState();
        showToast({
          title: 'User is busy',
          description: 'They are already on another call.',
          icon: createElement(PhoneOff, { className: 'h-5 w-5 text-gray-500' }),
        });
      }
    });
    return unsub;
  }, [
    clearCallTimeout,
    flushPendingCandidates,
    resetCallState,
    sendSignal,
    showToast,
    startCallTimeout,
    stopSounds,
    subscribe,
  ]);

  // Cleanup on unmount
  useEffect(() => () => resetCallState(), [resetCallState]);

  return (
    <VideoCallContext.Provider
      value={{
        status,
        connectionPhase,
        remoteUser,
        startCall,
        acceptCall,
        rejectCall,
        endCall,
        muted,
        cameraOff,
        toggleMute,
        toggleCamera,
        facingMode,
        canSwitchCamera,
        switchingCamera,
        switchCamera,
        zoomPresets,
        activeZoom,
        applyZoom,
        swapped,
        toggleSwap,
        videoFit,
        toggleFit,
      }}
    >
      {children}
      {status === 'incoming' && remoteUser && (
        <IncomingCallModal user={remoteUser} onAccept={acceptCall} onDecline={rejectCall} />
      )}
      {(status === 'active' || status === 'outgoing') && remoteUser && (
        <ActiveCallView
          status={status}
          connectionPhase={connectionPhase}
          remoteUser={remoteUser}
          localStream={localStream}
          remoteStream={remoteStream}
          muted={muted}
          cameraOff={cameraOff}
          onToggleMute={toggleMute}
          onToggleCamera={toggleCamera}
          onEndCall={endCall}
          facingMode={facingMode}
          canSwitchCamera={canSwitchCamera}
          switchingCamera={switchingCamera}
          onSwitchCamera={switchCamera}
          zoomPresets={zoomPresets}
          activeZoom={activeZoom}
          onZoom={applyZoom}
          swapped={swapped}
          onToggleSwap={toggleSwap}
          videoFit={videoFit}
          onToggleFit={toggleFit}
        />
      )}
    </VideoCallContext.Provider>
  );
}
